import { useWSModal, WSList, WSLoader } from "@wingspanhq/fe-component-library";
import type { cardApiSpec } from "@wingspanhq/wingspan-api-types";
import React, { useEffect, useMemo, useRef } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationCreateCard } from "../../../../query/cards/mutations/useMutationCreateCard";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { ErrorContextKey } from "../../../../services/platform";
import { selectorCardShippingAddress } from "../../selectors/selectorCardShippingAddress";

const INVALID_ADDRESS_ERROR =
  "We don't have a valid address on file for you. Please update your address in settings and try again.";

type CreateVirtualCardModalProps = {
  onClose: () => void;
  onSuccess: (card: cardApiSpec.Card) => void;
};

const CreateVirtualCardModal: React.FC<CreateVirtualCardModalProps> = props => {
  const userId = useUserId();
  const queryMemberProfile = useQueryMemberProfile(userId);
  const [createCard, createCardMeta] = useMutationCreateCard();
  const isCardRequested = useRef(false);

  const memberProfile = queryMemberProfile.data;
  const address = useMemo(
    () => selectorCardShippingAddress(memberProfile?.profile.address),
    [memberProfile]
  );

  useEffect(() => {
    if (!address || isCardRequested.current) {
      return;
    }

    isCardRequested.current = true;

    createCard(
      {
        isVirtualCard: true,
        userId,
        address
      },
      {
        onSuccess: card => {
          props.onSuccess(card);
          props.onClose();
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const error =
    queryMemberProfile.error ||
    createCardMeta.error ||
    (memberProfile && !address ? INVALID_ADDRESS_ERROR : undefined);

  return (
    <WSList gap="L">
      {error ? (
        <WSErrorMessage
          mb="XL"
          error={error}
          contextKey={ErrorContextKey.BankCard}
        />
      ) : (
        <WSLoader.Spinner />
      )}
    </WSList>
  );
};

export const useCreateVirtualCardModal = () =>
  useWSModal(CreateVirtualCardModal, {
    title: "Activate virtual card",
    size: "S"
  });
